import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

/**
 * Self-service change-password dialog. Verifies current password server-side.
 * Extracted from AppLayout.js — same DOM, same data-testids.
 */
export default function ChangePasswordDialog({ open, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setCurrent(""); setNext(""); setConfirm(""); }
  }, [open]);

  const submit = async () => {
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("New passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed");
      onClose && onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose} data-testid="change-password-dialog">
      <div className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-br from-purple-50 via-fuchsia-50 to-indigo-50">
          <div className="flex items-center gap-2 text-purple-800 font-semibold">
            <KeyRound className="h-5 w-5" /> Change password
          </div>
          <div className="text-xs text-slate-600 mt-1">
            You'll stay signed in with the new password.
          </div>
        </div>
        <div className="px-6 py-4 space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-700">Current password</label>
            <input
              autoFocus
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
              data-testid="pw-current"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">New password <span className="text-slate-400">(min 8)</span></label>
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
              data-testid="pw-new"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Confirm new password</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
              data-testid="pw-confirm"
            />
            {confirm && next !== confirm && (
              <div className="text-[11px] text-red-600 mt-1">Passwords don't match</div>
            )}
          </div>
        </div>
        <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="pw-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={busy || !current || !next || !confirm}
            className="px-3 py-1.5 rounded-md text-sm bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="pw-confirm-btn"
          >
            {busy ? "Saving…" : "Change password"}
          </button>
        </div>
      </div>
    </div>
  );
}
